import { CLIENT_BUILD } from "./client-build";
import { answerLiveVoiceQueries } from "./live-voice-presence";

export const SERVICE_WORKER_PATH = "/sw.js";
export const SERVICE_WORKER_SCOPE = "/";
export const SERVICE_WORKER_CACHE_PREFIX = "hrokbot-";
/** A long-lived tab re-checks for a new worker at most this often. */
export const SERVICE_WORKER_UPDATE_INTERVAL_MS = 30 * 60_000;
/** A worker that activates without sending `controllerchange` still gets the page reloaded. */
export const SERVICE_WORKER_RELOAD_FALLBACK_MS = 3_000;

export type ServiceWorkerAvailability = {
  production: boolean;
  hasServiceWorker: boolean;
};

/** Service workers exist only in production builds; development unregisters them instead. */
export function shouldRegisterServiceWorker({
  production,
  hasServiceWorker,
}: ServiceWorkerAvailability): boolean {
  return production && hasServiceWorker;
}

/** The build id is part of the script URL, so a deployment cannot reuse an older worker. */
export function serviceWorkerUrl(build: string): string {
  return `${SERVICE_WORKER_PATH}?v=${encodeURIComponent(build.trim() || "app")}`;
}

export type ServiceWorkerRegistrationLike = {
  readonly active: Pick<ServiceWorker, "scriptURL"> | null;
  readonly installing: Pick<ServiceWorker, "scriptURL"> | null;
  readonly waiting: Pick<ServiceWorker, "scriptURL"> | null;
  readonly scope: string;
  unregister(): Promise<boolean>;
};

export type ServiceWorkerRegistryLike = {
  getRegistrations(): Promise<readonly ServiceWorkerRegistrationLike[]>;
};

export type CacheStorageLike = Pick<CacheStorage, "keys" | "delete">;

function registrationScriptUrl(registration: ServiceWorkerRegistrationLike): string | undefined {
  return (
    registration.active?.scriptURL ??
    registration.waiting?.scriptURL ??
    registration.installing?.scriptURL
  );
}

function isAppServiceWorker(registration: ServiceWorkerRegistrationLike): boolean {
  const scriptUrl = registrationScriptUrl(registration);
  if (!scriptUrl) return false;
  try {
    return new URL(scriptUrl).pathname === SERVICE_WORKER_PATH;
  } catch {
    return false;
  }
}

/**
 * Development must never keep an installed worker: it would serve the app shell
 * from a cache while Vite expects every module to come from the dev server.
 */
export async function unregisterServiceWorker(
  registry: ServiceWorkerRegistryLike | undefined = typeof navigator === "undefined"
    ? undefined
    : navigator.serviceWorker,
  cacheStorage: CacheStorageLike | undefined = typeof caches === "undefined" ? undefined : caches,
): Promise<void> {
  try {
    const registrations = (await registry?.getRegistrations()) ?? [];
    await Promise.all(
      registrations.filter(isAppServiceWorker).map((registration) => registration.unregister()),
    );
  } catch {
    // A browser that refuses service worker access has nothing to unregister.
  }
  try {
    if (!cacheStorage) return;
    const keys = await cacheStorage.keys();
    await Promise.all(
      keys
        .filter((key) => key.startsWith(SERVICE_WORKER_CACHE_PREFIX))
        .map((key) => cacheStorage.delete(key)),
    );
  } catch {
    // Cache storage can be unavailable in private modes; the app still loads.
  }
}

let updateAvailable = false;
let waitingWorker: ServiceWorker | null = null;
let reloadPending = false;
let controllerListenerInstalled = false;
const trackedWorkers = new WeakSet<ServiceWorker>();
const updateListeners = new Set<() => void>();

function notifyUpdateListeners(): void {
  for (const listener of updateListeners) listener();
}

function setWaitingWorker(worker: ServiceWorker | null, controller: ServiceWorker | null): void {
  waitingWorker = worker;
  const next = Boolean(worker && controller);
  if (next === updateAvailable) return;
  updateAvailable = next;
  notifyUpdateListeners();
}

/** `useSyncExternalStore` contract for the reload banner. */
export function subscribeToServiceWorkerUpdate(listener: () => void): () => void {
  updateListeners.add(listener);
  return () => {
    updateListeners.delete(listener);
  };
}

export function getServiceWorkerUpdateAvailable(): boolean {
  return updateAvailable;
}

/**
 * A worker that is installing over an existing controller is waiting for the
 * page to allow it to take over. The first install has no controller and is
 * not an update, so it never shows the banner.
 */
export function watchServiceWorker(registration: ServiceWorkerRegistration): void {
  const container = navigator.serviceWorker;
  if (!controllerListenerInstalled) {
    container.addEventListener("controllerchange", () => {
      if (!reloadPending) return;
      reloadPending = false;
      window.location.reload();
    });
    controllerListenerInstalled = true;
  }
  if (registration.waiting) setWaitingWorker(registration.waiting, container.controller);
  trackInstalling(registration.installing, registration, container);
  registration.addEventListener("updatefound", () => {
    trackInstalling(registration.installing, registration, container);
  });
}

function trackInstalling(
  installing: ServiceWorker | null,
  registration: ServiceWorkerRegistration,
  container: ServiceWorkerContainer,
): void {
  if (!installing || trackedWorkers.has(installing)) return;
  trackedWorkers.add(installing);
  const installed = () =>
    setWaitingWorker(registration.waiting ?? installing, container.controller);
  if (installing.state === "installed") {
    installed();
    return;
  }
  installing.addEventListener("statechange", () => {
    if (installing.state === "installed") installed();
  });
}

/**
 * Ask the waiting worker to activate. The page reloads once it controls this
 * client, or after a short fallback for browsers that skip the event. The
 * pending flag is consumed exactly once, so an update cannot reload in a loop.
 */
export function applyServiceWorkerUpdate(): boolean {
  if (!waitingWorker) return false;
  reloadPending = true;
  try {
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
  } catch {
    reloadPending = false;
    return false;
  }
  window.setTimeout(() => {
    if (!reloadPending) return;
    reloadPending = false;
    window.location.reload();
  }, SERVICE_WORKER_RELOAD_FALLBACK_MS);
  return true;
}

function scheduleUpdateChecks(registration: ServiceWorkerRegistration): void {
  let lastCheck = Date.now();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastCheck < SERVICE_WORKER_UPDATE_INTERVAL_MS) return;
    lastCheck = Date.now();
    void registration.update().catch(() => undefined);
  });
}

export type InstallServiceWorkerOptions = {
  production?: boolean;
  build?: string;
};

/**
 * Registers the versioned worker in production and removes it in development.
 * A refused registration (insecure context, private mode) is not fatal: the
 * app simply runs without offline support.
 */
export async function installServiceWorker(
  options: InstallServiceWorkerOptions = {},
): Promise<ServiceWorkerRegistration | null> {
  const container = typeof navigator === "undefined" ? undefined : navigator.serviceWorker;
  const production = options.production ?? import.meta.env.PROD;
  if (!shouldRegisterServiceWorker({ production, hasServiceWorker: Boolean(container) })) {
    if (!production) await unregisterServiceWorker();
    return null;
  }
  if (!container) return null;
  answerLiveVoiceQueries(container);
  try {
    const registration = await container.register(serviceWorkerUrl(options.build ?? CLIENT_BUILD), {
      scope: SERVICE_WORKER_SCOPE,
      updateViaCache: "none",
    });
    watchServiceWorker(registration);
    scheduleUpdateChecks(registration);
    return registration;
  } catch {
    return null;
  }
}
